// Shared primitives for every dealer-facing OEM program surface (invoice
// breakdown, eligibility card, tracking dashboard). Keep the flag gate and
// ledger enum here so surfaces can't drift on the config key or its default.

/**
 * Display gate for every dealer-facing program surface, read off the
 * customer's `effective_config` so a program-level value reaches every
 * enrolled dealer and a customer-level value overrides it. Default off.
 * Billing runs regardless; this only controls what is shown.
 *   { program_visuals: { active: true, show_recoup: true } }
 * `show_recoup` additionally gates the "you owe the program" line.
 *
 * The raw `customers.config` holds only what was set on the customer itself,
 * so a program-level setting never appears in it.
 */
export const PROGRAM_VISUALS_CONFIG_KEY = 'program_visuals';

export type ProgramVisualsConfig = {
  active?: boolean;
  show_recoup?: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Takes the merged config as raw jsonb and keeps only boolean flags. Every
 * surface compares the flags with `=== true`, so a non-boolean value reads as
 * off either way.
 */
export const resolveProgramVisuals = (effectiveConfig?: unknown): ProgramVisualsConfig => {
  const section = isRecord(effectiveConfig) ? effectiveConfig[PROGRAM_VISUALS_CONFIG_KEY] : undefined;
  if (!isRecord(section)) return {};
  const visuals: ProgramVisualsConfig = {};
  if (typeof section.active === 'boolean') visuals.active = section.active;
  if (typeof section.show_recoup === 'boolean') visuals.show_recoup = section.show_recoup;
  return visuals;
};

export const PROGRAM_PAGE_ACCESS = {
  LOADING: 'loading',
  SHOW: 'show',
  REDIRECT: 'redirect',
} as const;

export type ProgramPageAccess = (typeof PROGRAM_PAGE_ACCESS)[keyof typeof PROGRAM_PAGE_ACCESS];

/**
 * Whether a program page shows, waits or sends the viewer away. It waits until the customer's
 * config has loaded: on a fresh load the flag reads as off until then, and deciding early would
 * redirect a customer who is in a program.
 */
export const resolveProgramPageAccess = (configLoaded: boolean, effectiveConfig?: unknown): ProgramPageAccess => {
  if (!configLoaded) return PROGRAM_PAGE_ACCESS.LOADING;
  return resolveProgramVisuals(effectiveConfig).active === true ? PROGRAM_PAGE_ACCESS.SHOW : PROGRAM_PAGE_ACCESS.REDIRECT;
};

/** program_ledger.type values (mirror the DB CHECK enum). */
export const LEDGER_TYPE = {
  PROGRAM_PAYMENT: 'program_payment',
  REIMBURSEMENT: 'reimbursement',
  RECOUP: 'recoup',
} as const;

export type LedgerType = (typeof LEDGER_TYPE)[keyof typeof LEDGER_TYPE];
